import { EventTimeline, type Spine, type TrackEntry } from '@esotericsoftware/spine-pixi-v8';
import { MagicLayout } from '@magic/layout';
import { wait } from '@magic/utils';
import { getGameConfig } from 'config/game.config';
import type { SymbolCell } from '../../utils/api.types';

/**
 * Animations of the anticipation overlay spine. The `_next_*` parts are the
 * layout's chaining convention, but the overlays are driven directly on their
 * own AnimationState (see `collectReelAnticipations`), so here they are just
 * names: the intro fades the overlay in, idle holds it (looped), end fades
 * it out.
 */
const anticipationAnimations = {
    intro: 'anticipation_next_anticipation_idle',
    idle: 'anticipation_idle_next_anticipation_end',
    end: 'anticipation_end',
};

/** Poll interval (ms) used while waiting on the spine clock — roughly one frame. */
const frameTime = 16;
/** Safety net (ms) so a stalled reel can never hang the reveal sequence. */
const cycleEndTimeout = 1000;

type SpineEventListener = (event: unknown) => void;

/**
 * Drives the reel spin cycle.
 *
 * Each reel spine holds `rows + 2` symbol slots (`spine_symbol0`…`spine_symbolN`):
 * slot 0 is the hidden buffer above the visible window, slots 1…rows are visible,
 * and the last slot is the hidden buffer below. Backends differ on whether the
 * board carries those buffers: the demo books ship `rows + 2` entries per reel
 * (row == slot), while the live RGS sends only the visible rows. Boards are
 * normalized to the padded form on arrival (see `normalizeBoard`) so the slot
 * mapping stays 1:1, and API positions (winInfo, freeSpinTrigger) shift by the
 * same detected offset when they are resolved to symbols.
 *
 * The reel asset has a single spin animation (`spin_<n>_rows`). While it plays,
 * the spine fires `update/symbol-X` each time slot X passes the hidden top
 * position — that is the safe moment to swap its skin. Each reel loops the spin
 * animation with random symbols until the backend result is in, then plays one
 * final cycle during which the update events place the result symbols and stops
 * without repeating the spin. Reels are released to stop leftmost first,
 * each one `config.reelStopDelay` ms after the previous.
 *
 * NB: a cycle is one full rotation *plus* a closing step that carries the reel
 * one slot further, so playing the animation to its very end leaves the reel a
 * slot past the setup pose. The final cycle is therefore cut at the rotation
 * mark — the last `update/symbol-X` event, which the asset keys exactly where
 * the reel passes through the setup pose (see `detectCycleAlignment`). Symbol X
 * then holds its own board row X, so the reel comes to rest with the result
 * already in place and the pin that follows changes nothing on screen.
 *
 * Games can export an `anticipation` overlay spine (the layout nests one
 * copy per reel via the reel's `spine_anticipation` slot); reels the backend
 * flags in the reveal's `anticipation` array then tease — spin longer with
 * the overlay playing — before they stop. See `collectReelAnticipations`.
 */
export class ReelController {
    /** reel spineID -> symbol spineIDs indexed by slot (0 = hidden top buffer). */
    private reelSymbols: Map<string, string[]> = new Map();
    /** reel spineIDs in board order (reel index 0 first). */
    private reelOrder: string[] = [];
    /** reel spineID -> its own anticipation overlay (empty when the game exports none). */
    private reelAnticipations: Map<string, Spine> = new Map();

    private resultBoard: SymbolCell[][] | null = null;
    /** 1 when the API board omits the hidden buffer rows (row 0 = top visible row). */
    private rowOffset = 0;
    /** Per-reel anticipation flags of the round being revealed (from the backend). */
    private anticipation: number[] | null = null;
    /** Reels currently playing their final, result-placing spin cycle. */
    private placingReels: Set<string> = new Set();
    /** Index of the reel released to stop next; -1 while no result is in. */
    private stopIndex = -1;
    private spinning = false;
    private spinPromise: Promise<void> | null = null;

    private readonly config = getGameConfig();
    private readonly slotsPerReel = this.config.rows + 2;
    private spinAnimation = `spin_${this.config.rows}_rows`;
    /** Track time the spin cycle completes its rotation at; 0 when unknown. */
    private cycleAlignTime = 0;

    constructor(private spine: MagicLayout) {
        this.detectReelAnimations();
        this.attachSymbolsToReels();
        this.detectCycleAlignment();
        this.collectReelAnticipations();
        this.addSymbolEventListeners();
    }

    /**
     * The reel spine declares its own size in its animation names
     * (`spin_3_rows`, `spin_5_rows`, ...), so resolve them from what is
     * actually registered instead of trusting the config to match the export.
     */
    private detectReelAnimations() {
        const spin = this.spine.animations.getAll().find((name) => /^spin_\d+_rows$/.test(name));

        if (spin) this.spinAnimation = spin;
        else console.warn(`Spin animation not found, expected "${this.spinAnimation}"`);
    }

    /**
     * Finds the track time the spin cycle has completed a full rotation at,
     * i.e. where the reel passes back through its setup pose.
     *
     * The asset marks it for us: `update/symbol-X` fires as slot X reaches the
     * hidden top position, so the *last* of those events is the moment the slot
     * that started at the top has come all the way around — every symbol back
     * on its own slot. Anything the animation keys after that is the closing
     * step into the next rotation, which the final cycle must not play.
     */
    private detectCycleAlignment() {
        const animation = this.spine
            .getSpine(this.reelOrder[0])
            ?.skeleton.data.findAnimation(this.spinAnimation);

        this.cycleAlignTime = 0;

        animation?.timelines.forEach((timeline) => {
            if (!(timeline instanceof EventTimeline)) return;

            timeline.events.forEach((event) => {
                if (!/^update\/symbol-\d+$/.test(event.data.name)) return;

                this.cycleAlignTime = Math.max(this.cycleAlignTime, event.time);
            });
        });

        if (!this.cycleAlignTime) {
            console.warn(`No "update/symbol-X" events in "${this.spinAnimation}"`);
        }
    }

    /** Nests the shared symbol spine pool into the reel slots and records the mapping. */
    private attachSymbolsToReels() {
        const spineController = this.spine.spine;
        const symbols = spineController.getSpinesByNamePattern('symbol');
        // registry order follows the skeleton's slot order, which is not
        // guaranteed to be visual order (e.g. reel_5 can be declared first) —
        // sort by the numeric suffix so board reel 0 is the leftmost reel
        const reels = [...spineController.getSpinesByNamePattern('reel', { not: ['reels'] }).keys()]
            .sort((a, b) => this.reelNumberFromID(a) - this.reelNumberFromID(b));
        let counter = 0;

        reels.forEach((reelID) => {
            const slotSymbolIDs: string[] = [];

            for (let slot = 0; slot < this.slotsPerReel; slot++) {
                counter++;
                const symbolID = `symbol${counter}`;
                const symbol = symbols.get(symbolID);

                if (symbol) {
                    this.spine.scene.addSlotChild(reelID, `spine_symbol${slot}`, symbol);
                    slotSymbolIDs.push(symbolID);
                } else {
                    console.warn(`Symbol spine not found for counter: ${counter}`);
                }
            }

            this.reelOrder.push(reelID);
            this.reelSymbols.set(reelID, slotSymbolIDs);
        });
    }

    private reelNumberFromID(reelID: string): number {
        return Number(reelID.match(/(\d+)$/)?.[1] ?? 0);
    }

    /**
     * Collects each reel's anticipation overlay and parks it out of sight.
     * The layout multiplies the shared `anticipation` export into one
     * `anticipation_<reelID>` instance per reel (every reel carries the plain
     * `spine_anticipation` pointer) and nests each into its own reel, so
     * here they only need to be looked up.
     *
     * The overlays are driven directly on their own AnimationState, not via
     * the layout's animation controller: its `_next_*` chaining plays the
     * next animation by *name* across all spines, so one reel's tease would
     * trigger every other reel's overlay.
     */
    private collectReelAnticipations() {
        this.reelOrder.forEach((reelID) => {
            const overlay = this.spine.getSpine(`anticipation_${reelID}`);
            if (!overlay) return; // game without an anticipation spine

            const missing = Object.values(anticipationAnimations).filter(
                (name) => !overlay.skeleton.data.findAnimation(name),
            );
            if (missing.length) {
                console.warn(`Anticipation spine misses animations: ${missing.join(', ')}`);
                return;
            }

            this.parkAnticipation(overlay);
            this.reelAnticipations.set(reelID, overlay);
        });
    }

    /**
     * Holds an overlay on the end animation's last frame — every slot faded
     * out. The overlay's setup pose is fully visible and the parent reel
     * re-applies `visible` to its slot objects every frame, so an overlay is
     * hidden in spine terms: the finished entry stays on the track and keeps
     * applying the faded-out pose until the next tease replaces it.
     */
    private parkAnticipation(overlay: Spine) {
        const parked = overlay.state.setAnimation(0, anticipationAnimations.end, false);
        parked.trackTime = parked.animation?.duration ?? 0;
    }

    /** Fades a reel's anticipation overlay in and holds it on its idle loop. */
    private startAnticipation(reelID: string) {
        const overlay = this.reelAnticipations.get(reelID);
        if (!overlay) return;

        overlay.state.setAnimation(0, anticipationAnimations.intro, false);
        overlay.state.addAnimation(0, anticipationAnimations.idle, true, 0);
    }

    /** Fades a reel's anticipation overlay out, back to its parked end frame. */
    private stopAnticipation(reelID: string) {
        const overlay = this.reelAnticipations.get(reelID);
        const current = overlay?.state.getTrack(0);

        // already parked on (or fading towards) the end pose — not teasing
        if (!overlay || current?.animation?.name === anticipationAnimations.end) return;

        const end = overlay.state.setAnimation(0, anticipationAnimations.end, false);

        // the idle loop is interrupted mid-cycle, so blend into the fade
        end.mixDuration = 0.2;
    }

    private addSymbolEventListeners() {
        for (let slot = 0; slot < this.slotsPerReel; slot++) {
            const onUpdate = (reelID: string) =>
                this.setSlotSymbol(reelID, slot, this.getUpdateSymbolName(reelID, slot));

            this.spine.animations.addEventListener(
                `update/symbol-${slot}`,
                onUpdate as unknown as SpineEventListener,
            );
        }
    }

    /**
     * Starts every reel on its own spin loop. Each reel keeps looping the spin
     * animation (random symbols) until `reveal()` releases it to stop, and for
     * at least `config.minSpinTime` so the randomized symbols stay visible
     * even when the result arrives immediately, e.g. from the demo API.
     */
    spin() {
        if (this.spinning) return;
        this.spinning = true;
        this.stopIndex = -1;

        const minSpinEnd = Date.now() + this.config.minSpinTime;

        this.spinPromise = Promise.all(
            this.reelOrder.map((reelID, reel) => this.spinReel(reelID, reel, minSpinEnd)),
        ).then(() => undefined);
    }

    /**
     * One reel's spin lifecycle: loop random cycles until this reel is
     * released (`stopIndex` reaches it), then play one final cycle in placing
     * mode — its `update/symbol-X` events put the result symbols on the
     * visible slots — and stop without repeating the spin. Being released
     * schedules the next reel's release `config.reelStopDelay` later, so
     * the reels stop staggered instead of waiting on each other to finish.
     */
    private async spinReel(reelID: string, reel: number, minSpinEnd: number) {
        do {
            await this.spine.animations.play(reelID, this.spinAnimation);
        } while (this.stopIndex < reel || Date.now() < minSpinEnd);

        // the backend flags reels that should tease before stopping: show the
        // anticipation overlay and hold the reel on extra spin cycles. Later
        // reels wait on this reel's release, so flagged reels tease one after
        // another instead of all at once.
        if (this.anticipation?.[reel]) {
            this.startAnticipation(reelID);
            const anticipationEnd = Date.now() + this.config.anticipationSpinTime;

            do {
                await this.spine.animations.play(reelID, this.spinAnimation);
            } while (Date.now() < anticipationEnd);
        }

        wait(this.config.reelStopDelay).then(() => {
            this.stopIndex = Math.max(this.stopIndex, reel + 1);
        });

        this.placingReels.add(reelID);
        // play() runs setAnimation synchronously, so the cycle can be cut short
        // before it has advanced a frame — no need to await it first
        const cycle = this.spine.animations.play(reelID, this.spinAnimation);
        this.cutCycleAtAlignment(reelID);
        await cycle;

        // play() resolves on a wall-clock timer, not the spine's clock — the
        // pose can still be short of where the cycle ends when it does
        await this.waitForCycleEnd(reelID);

        // pin the reel: the pose is already the setup pose and every symbol
        // already holds its own board row, so this only drops the finished
        // track (no stale symbol events can fire afterwards) and re-asserts
        // the result in the same frame
        this.spine.animations.reset(reelID, this.spinAnimation);
        this.placingReels.delete(reelID);
        this.setReelBoard(reelID, reel, this.resultBoard);
        // the result is on screen — resolve the tease (no-op when not teasing)
        this.stopAnticipation(reelID);
    }

    /**
     * Ends a reel's final cycle on the rotation mark instead of the animation's
     * last keyframe, so the reel comes to rest on the setup pose.
     *
     * Left to run out, the cycle takes one more step past that mark and settles
     * a slot further on — the board would then sit one row too high, the bottom
     * visible row showing the hidden bottom padding row, until the pin snapped
     * everything into place a frame later. Shortening the track entry stops the
     * reel exactly where the result is already aligned; the events up to and
     * including the mark still fire, so every symbol is placed.
     */
    private cutCycleAtAlignment(reelID: string) {
        const entry = this.spinTrackEntry(reelID);

        if (!entry || !this.cycleAlignTime) return;

        entry.animationEnd = this.cycleAlignTime;
    }

    /**
     * Holds until a reel's spin cycle has actually run out on the spine's own
     * clock — `animations.play()` resolves on a wall-clock timer, which can be
     * a frame or two ahead of it, and the reel must not be pinned while it is
     * still mid-step.
     */
    private async waitForCycleEnd(reelID: string) {
        const deadline = Date.now() + cycleEndTimeout;

        while (Date.now() < deadline) {
            const entry = this.spinTrackEntry(reelID);

            // the track finished and was cleared, or something else took it
            // over — either way there is no cycle left to land
            if (!entry) return;
            if (entry.trackTime >= entry.animationEnd) return;

            await wait(frameTime);
        }

        console.warn(`Reel ${reelID} spin cycle did not reach its end pose`);
    }

    /** The reel's currently applied spin cycle, on whichever track it was played. */
    private spinTrackEntry(reelID: string): TrackEntry | null {
        const spine = this.spine.getSpine(reelID);
        const entry = spine?.state.tracks.find(
            (track) => track?.animation?.name === this.spinAnimation,
        );

        return entry ?? null;
    }

    /**
     * Hands the result board to the spin loop and releases the first reel.
     * The reels then fill with the result and stop one by one; when this
     * resolves the visible board matches `board`. When the reels are not
     * spinning (e.g. a restored round on load) the board is applied
     * instantly instead.
     *
     * `anticipation` holds the backend's per-reel flags: a reel with a
     * non-zero value plays its anticipation overlay and spins longer before
     * it is allowed to stop.
     */
    async reveal(board: SymbolCell[][], anticipation: number[] = []) {
        const normalized = this.normalizeBoard(board);

        if (!this.spinPromise) {
            this.applyBoard(normalized);
            return;
        }

        this.resultBoard = normalized;
        this.anticipation = anticipation;
        this.stopIndex = 0;

        await this.spinPromise;
        this.spinPromise = null;
        this.spinning = false;
        this.resultBoard = null;
        this.anticipation = null;
    }

    /**
     * Releases the reels with no result (they settle on random symbols), for
     * when a spin request fails and no board will arrive. No-op when idle.
     */
    async stop() {
        if (!this.spinning) return;
        await this.reveal([]);
    }

    /**
     * Normalizes an API board to the padded, slot-aligned form and records the
     * row offset API positions need when resolved to symbols. A board without
     * the hidden buffer rows (`rows` entries per reel) gains a random buffer
     * cell above and below each reel; a padded board passes through unchanged.
     */
    private normalizeBoard(board: SymbolCell[][]): SymbolCell[][] {
        const unpadded =
            board.length > 0 && board.every((column) => column.length === this.config.rows);

        this.rowOffset = unpadded ? 1 : 0;

        if (!unpadded) return board;

        return board.map((column) => [
            { name: this.getRandomSymbolName() },
            ...column,
            { name: this.getRandomSymbolName() },
        ]);
    }

    /** Applies a result board instantly (no spin), e.g. when restoring a round on load. */
    setBoard(board: SymbolCell[][]) {
        this.applyBoard(this.normalizeBoard(board));
    }

    /** Pins every reel to an already-normalized board. */
    private applyBoard(board: SymbolCell[][]) {
        this.reelOrder.forEach((reelID, reel) => this.setReelBoard(reelID, reel, board));
    }

    /** Pins one reel's slots to the board result (board rows map 1:1 to slots). */
    private setReelBoard(reelID: string, reel: number, board: SymbolCell[][] | null) {
        for (let slot = 0; slot < this.slotsPerReel; slot++) {
            const cell = board?.[reel]?.[slot];
            this.setSlotSymbol(reelID, slot, cell?.name ?? this.getRandomSymbolName());
        }
    }

    /** Fills every slot of every reel with a random symbol (initial idle board). */
    fillBoardWithRandomSymbols() {
        this.reelSymbols.forEach((slotSymbolIDs) => {
            slotSymbolIDs.forEach((symbolID) => {
                this.spine.skins.applyBySpineID(symbolID, this.getRandomSymbolName());
            });
        });
    }

    /**
     * Resolves an API position (0-based reel/row, in the round's own board
     * convention — see `normalizeBoard`) to the symbol spineID in the visible
     * window. Positions that fall outside it return '' — they must never
     * resolve to the hidden buffer slots.
     */
    getSymbolIDByPosition(reel: number, row: number): string {
        const slot = row + this.rowOffset;

        if (!this.isVisibleSlot(slot)) return '';

        return this.reelSymbols.get(this.reelOrder[reel])?.[slot] ?? '';
    }

    /** Symbol spineIDs currently in the visible window (slots 1…rows), all reels. */
    getVisibleSymbolIDs(): string[] {
        return this.reelOrder.flatMap((reelID) =>
            (this.reelSymbols.get(reelID) ?? []).filter((_, slot) => this.isVisibleSlot(slot)),
        );
    }

    /** Whether slot X (== board row X) is inside the visible window at the setup pose. */
    private isVisibleSlot(slot: number): boolean {
        return slot >= 1 && slot <= this.config.rows;
    }

    /**
     * Symbol for an `update/symbol-X` event: random while the reel spins; on
     * the reel's final, result-placing cycle slot X receives its own board row,
     * because that cycle is cut where the reel is back on the setup pose (see
     * `cutCycleAtAlignment`).
     */
    private getUpdateSymbolName(reelID: string, slot: number): string {
        if (!this.placingReels.has(reelID)) return this.getRandomSymbolName();

        const reel = this.reelOrder.indexOf(reelID);
        const cell = this.resultBoard?.[reel]?.[slot];

        return cell?.name ?? this.getRandomSymbolName();
    }

    private setSlotSymbol(reelID: string, slot: number, symbolName: string) {
        const symbolID = this.reelSymbols.get(reelID)?.[slot];
        if (!symbolID) return;

        this.spine.skins.applyBySpineID(symbolID, symbolName);
    }

    private getRandomSymbolName(): string {
        const symbols = this.config.symbols;
        return symbols[Math.floor(Math.random() * symbols.length)];
    }
}
